/**
 * Equipment Register CSV Export Utility
 * Exports equipment register to CSV format for spreadsheet analysis
 */

/**
 * Escape CSV field values to handle commas, quotes, newlines
 */
function escapeCSVField(value) {
  if (value == null) return '';

  const stringValue = String(value);

  // If field contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return '"' + stringValue.replace(/"/g, '""') + '"';
  }

  return stringValue;
}

/**
 * Format date for CSV export
 */
function formatDate(dateValue) {
  if (!dateValue) return '';

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';

    // Return ISO date format (YYYY-MM-DD)
    return date.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
}

/**
 * Export equipment items to CSV format
 * @param {Array} items - Equipment items to export
 * @param {Object} vessel - Current vessel info
 * @returns {string} CSV content
 */
export function generateEquipmentCSV(items, vessel = {}) {
  // CSV Headers
  const headers = [
    'Category',
    'Name',
    'Type',
    'Location',
    'Serial Number',
    'Manufacturer',
    'Model',
    'Install Date',
    'Last Service',
    'Next Service',
    'Status',
    'Notes'
  ];

  // Build CSV rows
  const rows = items.map(item => {
    return [
      escapeCSVField(item.category || ''),
      escapeCSVField(item.name || ''),
      escapeCSVField(item.type || ''),
      escapeCSVField(item.location || ''),
      escapeCSVField(item.serialNumber || ''),
      escapeCSVField(item.manufacturer || ''),
      escapeCSVField(item.model || ''),
      formatDate(item.installDate),
      formatDate(item.lastServiceDate),
      formatDate(item.nextServiceDate),
      escapeCSVField(item.status || 'Active'),
      escapeCSVField(item.notes || '')
    ].join(',');
  });

  // Combine headers and rows
  const csvContent = [
    `# Equipment Register Export - ${vessel.name || 'Unknown Vessel'}`,
    `# Exported: ${new Date().toISOString()}`,
    `# Total Items: ${items.length}`,
    '', // Empty line before data
    headers.join(','),
    ...rows
  ].join('\n');

  return csvContent;
}

/**
 * Download equipment register as CSV file
 * @param {Array} items - Equipment items to export
 * @param {Object} vessel - Current vessel info
 */
export function downloadEquipmentCSV(items, vessel = {}) {
  const csvContent = generateEquipmentCSV(items, vessel);

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `equipment_register_${vessel.name || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up URL
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
